import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { StudyReadyEvent } from 'helper/flux/diffStudiesStatus';
import { UseMyStudiesPageResult } from 'utils/hooks/flux/personalized/useMyStudiesPage';
import useMyStudiesStatusPolling from 'utils/hooks/flux/personalized/useMyStudiesStatusPolling';

type AutoRefreshPageField = 'listArgs' | 'state' | 'hasListOnScreen' | 'lastUpdatedAt' | 'refetchPage';

/** O que a atualização automática usa do `useMyStudiesPage` da tela. */
export type AutoRefreshPage = Pick<UseMyStudiesPageResult, AutoRefreshPageField>;

/** Ação da linha concluída com sucesso (o `StudyActionKind` da `ActionsCell`). */
export type StudyActionOutcome = 'resend' | 'cancel' | 'delete';

export interface UseMyStudiesAutoRefreshOptions {
  /** O resultado do `useMyStudiesPage` da casca (instância única). */
  page: AutoRefreshPage;
  /** Liga o polling: o `!skip` da lista (usuário válido). A casca só existe no desktop (D14). */
  enabled: boolean;
  /** Recebe cada lote de estudos prontos (`useActionFeedback().showReadyNotices`). Estável. */
  onReadyEvents: (events: readonly StudyReadyEvent[]) => void;
}

export interface UseMyStudiesAutoRefreshResult {
  /** Estudo ativo na página (`polling.hasActive`): bolinha verde e "Atualização automática". */
  hasActive: boolean;
  /**
   * Hora do indicador: o `lastUpdatedAt` do polling (D03) com a lista assentada; no pending, a
   * hora da lista que continua na tela; sem lista na tela, `undefined`.
   */
  updatedAt: number | undefined;
  /** A lista (bloco ou faixa) ou o status falhou: bolinha vermelha e "Sem conexão". */
  offline: boolean;
  /** O polling de status falhou: a faixa aparece também com a lista em dia. */
  statusFailed: boolean;
  /** Hora da faixa "Status não atualizado desde HH:MM.": o `lastUpdatedAt` do polling. */
  staleSince: number | undefined;
  /**
   * Muda só quando o status falha depois de um "Tentar de novo" pedido com ele em erro: a faixa
   * remonta e o `role="alert"` anuncia de novo. As falhas dos ciclos automáticos (offline, a cada
   * ~32 s) mantêm a chave, para o leitor de tela não repetir a faixa.
   */
  staleBannerKey: string;
  /** "Tentar de novo" da faixa e do bloco: refaz o que falhou. */
  retryFailed: () => void;
  /** "Atualizar" (D02): o `refetchPage`, 1 refetch da página atual. */
  refresh: () => void;
  /** `onActionSuccess` da lista: reenviar e cancelar destacam a linha (D12). Estável. */
  onActionSuccess: (studyId: number, kind: StudyActionOutcome) => void;
  /** Linhas destacadas (`polling.changedIds`), com identidade estável. */
  highlightedIds: ReadonlySet<number>;
}

/**
 * Atualização automática da aba Meus Estudos ligada à tela (#2004): monta o polling de status da
 * #2001 sobre a MESMA consulta da página (`listArgs`) e concentra as regras da casca.
 *
 * - **"Tentar de novo"** (faixa e bloco) refaz o que falhou: só o status falhou → `retry` do
 *   polling, sem refazer a lista; a lista falhou → `refetchPage` e, se o status também falhou, o
 *   `retry`. Sem falha nenhuma (lado seguro), refaz a lista.
 * - **Chave da faixa (`staleBannerKey`):** muda só quando o status falha de novo DEPOIS de um
 *   "Tentar de novo" (a faixa remonta e é anunciada outra vez, como resposta ao pedido). As falhas
 *   dos ciclos automáticos, offline, mantêm a mesma faixa: nada de anúncio a cada ~32 s.
 * - **Lista nova com o status em erro:** quando chega uma lista da página atual (o
 *   `fulfilledTimeStamp` do `useMyStudiesPage` mudou: outra página, outro filtro, "Atualizar",
 *   refetch por invalidação), o status é consultado na hora. Sem isso, a faixa e o "Sem conexão"
 *   ficariam até o próximo ciclo (30 s) sobre uma lista recém-chegada. Se o status continuar
 *   falhando, o erro fica e só a próxima lista nova reconsulta: sem laço.
 * - **"Atualizar"** é só o `refetchPage`: 1 refetch da página atual (D02). O status em erro é
 *   reconsultado pela regra acima, quando a lista chega.
 * - **Destaque (D12):** reenviar e cancelar com sucesso entram no destaque do polling
 *   (`highlightRows`, 2500 ms). Excluir não destaca: a linha sai.
 * - **Avisos de pronto:** cada lote novo de `readyEvents` vai para `onReadyEvents`.
 * - **Hora do indicador:** no pending de outra página ou filtro, vale a hora da lista que continua
 *   na tela (esmaecida), e não a da entrada de destino, que pode ter só um status antigo.
 *
 * @param options - A página, o `enabled` do polling e o destino dos avisos de pronto.
 * @returns O estado do indicador e da faixa, o destaque e as ações (ver
 *   {@link UseMyStudiesAutoRefreshResult}).
 */
export default function useMyStudiesAutoRefresh({
  page, enabled, onReadyEvents,
}: UseMyStudiesAutoRefreshOptions): UseMyStudiesAutoRefreshResult {
  const {
    listArgs, state, hasListOnScreen, lastUpdatedAt: listUpdatedAt, refetchPage,
  } = page;
  const {
    hasActive, lastUpdatedAt, lastFailureAt, error, retry: retryStatus, changedIds, readyEvents,
    highlightRows,
  } = useMyStudiesStatusPolling({ listArgs, enabled });

  const listFailed = state === 'error-initial' || state === 'error-stale';
  const statusFailed = error !== null;

  // Hora do indicador: guardada com a lista assentada, para o pending de outra página ou filtro
  const listSettled = state === 'ready' || state === 'error-stale';
  const [settledUpdatedAt, setSettledUpdatedAt] = useState(listSettled ? lastUpdatedAt : undefined);
  if (listSettled && lastUpdatedAt !== settledUpdatedAt) setSettledUpdatedAt(lastUpdatedAt);
  let updatedAt: number | undefined;
  if (hasListOnScreen) updatedAt = listSettled ? lastUpdatedAt : settledUpdatedAt;

  // Chave da faixa: só a falha que responde a um "Tentar de novo" a muda. `pendingRetry` guarda a
  // falha que estava na tela quando o usuário pediu o retry do status; a 1ª falha diferente dela
  // vira a chave e consome o pedido. Com o status de volta, o pedido some
  // `after`: a falha na tela no clique (`undefined` se ela veio de outra página)
  const [pendingRetry, setPendingRetry] = useState<{ after?: number } | null>(null);
  const [retriedFailureAt, setRetriedFailureAt] = useState<number>();
  const failedAfterRetry = pendingRetry !== null && lastFailureAt !== undefined
    && lastFailureAt !== pendingRetry.after;
  if (pendingRetry !== null && !statusFailed) setPendingRetry(null);
  if (failedAfterRetry) {
    setRetriedFailureAt(lastFailureAt);
    setPendingRetry(null);
  }

  const retryFailed = useCallback(() => {
    if (statusFailed) {
      setPendingRetry({ after: lastFailureAt });
      retryStatus();
    }
    if (listFailed || !statusFailed) refetchPage();
  }, [statusFailed, listFailed, lastFailureAt, retryStatus, refetchPage]);

  // Lista nova da página atual com o status em erro: consulta o status na hora
  const previousListAt = useRef(listUpdatedAt);
  useEffect(() => {
    const previous = previousListAt.current;
    previousListAt.current = listUpdatedAt;
    if (listUpdatedAt === undefined || listUpdatedAt === previous || !statusFailed) return;
    retryStatus();
  }, [listUpdatedAt, statusFailed, retryStatus]);

  const onActionSuccess = useCallback((studyId: number, kind: StudyActionOutcome) => {
    if (kind === 'resend' || kind === 'cancel') highlightRows([studyId]);
  }, [highlightRows]);

  useEffect(() => {
    if (readyEvents.length > 0) onReadyEvents(readyEvents);
  }, [readyEvents, onReadyEvents]);

  return {
    hasActive,
    updatedAt,
    offline: listFailed || statusFailed,
    statusFailed,
    staleSince: lastUpdatedAt,
    staleBannerKey: statusFailed ? `status:${retriedFailureAt ?? ''}` : 'list',
    retryFailed,
    refresh: refetchPage,
    onActionSuccess,
    highlightedIds: changedIds,
  };
}
